import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useQuery } from "convex/react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Ban, ClipboardCheck, Search, ShieldAlert, ShieldOff } from "lucide-react";
import { api } from "../../convex/_generated/api";
import { WlsLogo } from "../components/WlsLogo";
import { Badge, Button, Card, Input, Notice, PageHeader, TableSkeleton } from "../components/ui";
import { CATEGORY_LABELS, STATUS_LABELS, formatDate, formatNumber } from "../lib/format";

export function DashboardPage() {
  const summary = useQuery(api.dashboard.summary);
  const me = useQuery(api.users.me);
  const staff = me ? me.role !== "visitor" : false;
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const submitted = useRef(false);
  const goToSearch = useCallback((value: string) => {
    const query = value.trim();
    if (query) navigate(`/blacklist?q=${encodeURIComponent(query)}`);
  }, [navigate]);
  // La recherche part d'elle-même, mais seulement une fois la saisie posée : basculer de page
  // dès la première lettre arracherait le tableau de bord sous les doigts, et une seule lettre
  // ne ramène de toute façon rien d'exploitable.
  useEffect(() => {
    if (submitted.current) return;
    const query = search.trim();
    if (query.length < 3) return;
    const timer = setTimeout(() => goToSearch(query), 400);
    return () => clearTimeout(timer);
  }, [search, goToSearch]);
  const counter = (value: number) => summary?.computedAt === 0 ? "—" : formatNumber(value);
  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // La validation manuelle passe outre le délai et le minimum de trois caractères.
    submitted.current = true;
    goToSearch(search);
  };
  return (
    <>
      <PageHeader title="Vue d’ensemble" description="L’état de la base et des derniers contrôles, en un coup d’œil." actions={staff ? <Link className="button button--primary" to="/controles">Lancer un contrôle <ArrowRight size={16} /></Link> : undefined} />
      <form className="card toolbar dashboard-search" onSubmit={submitSearch}>
        <div className="search-field">
          <Search size={17} />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher un Epic ID, Discord ID ou pseudo…"
            aria-label="Recherche rapide"
          />
        </div>
        <Button type="submit" disabled={!search.trim()}>Rechercher</Button>
      </form>
      {!summary ? <TableSkeleton /> : (
        <>
          {/* Tant que le premier recomptage n'a pas tourné, les compteurs valent zéro : afficher
              « 0 joueur blacklisté » ferait croire à une base vide. */}
          {summary.computedAt === 0 && (
            <Notice tone="info">Les compteurs sont en cours de calcul. Ils s’afficheront dès la fin du premier recomptage.</Notice>
          )}
          <div className="metric-grid">
            <Card className="metric-card"><span className="metric-icon"><Ban /></span><div><span>Joueurs blacklistés</span><strong>{counter(summary.active)}</strong></div></Card>
            <Card className="metric-card"><span className="metric-icon metric-icon--warning"><ShieldAlert /></span><div><span>Contournements de ban</span><strong>{counter(summary.evasions)}</strong></div></Card>
            <Card className="metric-card"><span className="metric-icon metric-icon--muted"><ShieldOff /></span><div><span>Sanctions levées</span><strong>{counter(summary.lifted)}</strong></div></Card>
            {staff && <Card className="metric-card"><span className="metric-icon metric-icon--success"><ClipboardCheck /></span><div><span>Contrôles récents</span><strong>{summary.recentChecks.length}</strong></div></Card>}
          </div>
          <div className="dashboard-grid">
            <Card>
              <div className="card-heading"><h2>Blacklist active</h2><Link to="/blacklist">Voir la base <ArrowRight size={15} /></Link></div>
              <div className="category-bars">
                {Object.entries(summary.byCategory).map(([category, count]) => {
                  const total = Math.max(summary.active, 1);
                  // Une catégorie vide n'a pas de barre : un trait minimal laisserait croire à des entrées.
                  return <div key={category}><div><span>{CATEGORY_LABELS[category as keyof typeof CATEGORY_LABELS]}</span><strong>{counter(count)}</strong></div><span className="bar"><i style={{ width: count > 0 ? `${Math.max(2, (count / total) * 100)}%` : 0 }} /></span></div>;
                })}
              </div>
            </Card>
            <Card>
              <div className="card-heading"><h2>Derniers contrôles</h2><Link to="/controles">Tous <ArrowRight size={15} /></Link></div>
              <div className="activity-list">
                {summary.recentChecks.length === 0 && <p className="muted">Aucun contrôle lancé pour le moment.</p>}
                {summary.recentChecks.map((check) => <Link to={`/controles/${check._id}`} key={check._id} className="activity-row"><span className="activity-row__mark" title={check.source === "csv" ? "Import CSV" : "Tournoi WLS"}>{check.source === "csv" ? "CSV" : <WlsLogo size={18} />}</span><span><strong>{check.tournamentId || check.fileName || "Contrôle"}</strong><small>{formatDate(check.createdAt)}</small></span><Badge tone={check.status === "failed" ? "danger" : check.status === "completed" ? "success" : "warning"}>{STATUS_LABELS[check.status]}</Badge></Link>)}
              </div>
            </Card>
          </div>
        </>
      )}
    </>
  );
}
